import { Input, Form, Button } from 'antd'
import Checkbox from 'antd/lib/checkbox/Checkbox'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ERROR_CODES, ROLES, STATUS_LOGIN } from '../../helpers/constants'
import { GLOBAL_CLIENT } from '../../helpers/GlobalClient'
import { useStore } from '../../hooks'
import { rule } from '../../helpers/validateRule'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import {
   accountantMeta,
   adminMeta,
   fulfillmentMeta,
   managerOrderMeta,
   managerStockMeta,
   sellerMeta,
   supplierMeta
} from '../routes/metaData'
import Images from 'src/common/Images'
import { showMessageError } from 'src/helpers/functions'
import { debounce } from 'lodash'
import { AuthenWrapper, LoginWrapper } from 'src/styles/login/Authen.style'
import BaseButton from 'src/common/components/button'

const LoginScreen = observer((props: any) => {
   const AuthStore = useStore('AuthStore')
   const { t } = useTranslation()
   const [isLoading, setIsLoading] = useState(false)
   const [rememberMe, setRememberMe] = useState(true)
   const { history } = props
   const [form] = Form.useForm()
   const [otpCode, setOtpCode] = useState('')
   const [isVerifying2FA, setIsVerifying2FA] = useState(false)

   useEffect(() => {
      if (AuthStore.isLogin === STATUS_LOGIN.LOGIN_SUCCESS) history.push('/')
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [AuthStore.isLogin])

   const onChangeRememberMe = (e: any) => {
      setRememberMe(e.target.checked)
   }

   const navigateByRole = (role: string) => {
      if (role == ROLES.ADMIN) {
         history.push(adminMeta[0].path)
      } else if (role == ROLES.SELLER) {
         history.push(sellerMeta[0].path)
      } else if (role == ROLES.FULFILLMENT) {
         history.push(fulfillmentMeta[0].path)
      } else if (role == ROLES.MANAGER_ORDER) {
         history.push(managerOrderMeta[0].path)
      } else if (role == ROLES.MANAGER_STOCK) {
         history.push(managerStockMeta[0].path)
      } else if (role == ROLES.ACCOUNTANT) {
         history.push(accountantMeta[0].path)
      } else if (role == ROLES.NCC) {
         history.push(supplierMeta[0].path)
      } else {
         history.push(supplierMeta[0].path)
      }
   }

   const onFinishFailed = (errorInfo: any) => {}

   const handleVerify2FA = async () => {
      if (!otpCode || otpCode.length !== 6) return
      try {
         setIsVerifying2FA(true)
         const result = await AuthStore.action_verify2FA(otpCode, rememberMe)
         if (result?.code === ERROR_CODES.SUCCESS) {
            navigateByRole(result?.data?.account?.role)
         }
      } catch (error) {
         // handled by Request2
      } finally {
         setIsVerifying2FA(false)
      }
   }

   const onFinish = async (values: any) => {
      try {
         setIsLoading(true)
         Object.keys(values)?.forEach((k) => {
            if (typeof values[k] == 'string') return (values[k] = values[k].trim())
         })
         const { email, password } = values
         const result = await AuthStore.action_loginUser(email, password, rememberMe)
         if (result?.code === ERROR_CODES.SUCCESS) {
            // Nếu cần 2FA, không điều hướng - UI sẽ hiển thị form OTP
            if (result?.data?.requires2FA) {
               return
            }
            navigateByRole(result?.data?.account?.role)
         } else if (result?.isAccountNotActive) {
            showMessageError(t(GLOBAL_CLIENT.accountNotActive), () => history.push('/resend-verify-email'))
         }
      } catch (error) {
         // console.log(error)
      } finally {
         setIsLoading(false)
      }
   }

   return (
      <AuthenWrapper>
         {/* <Loading isLoading={isLoading} /> */}

         <div className="login__banner">
            <img src={Images.bg_login} alt="" />
         </div>
         <LoginWrapper className="login__content">
            {/* <StickyChatButton /> */}
            <div className="login-content__body">
               <div className="login-content__logo">
                  <img src={Images.logo_white} alt="" />
               </div>
               <div className="login-content__form">
                  {AuthStore.requires2FA ? (
                     <>
                        <div className="form__title">Xác thực 2 bước</div>
                        <div style={{ marginBottom: 16, color: '#666', fontSize: 14 }}>
                           Nhập mã OTP 6 chữ số từ ứng dụng Authenticator
                        </div>
                        <Input
                           maxLength={6}
                           placeholder="Nhập mã OTP"
                           value={otpCode}
                           onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                           onPressEnter={handleVerify2FA}
                           prefix={<LockOutlined />}
                           style={{ fontSize: 18, textAlign: 'center', letterSpacing: 6, marginBottom: 16 }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                           <Button
                              type="primary"
                              style={{ flex: 1 }}
                              onClick={handleVerify2FA}
                              loading={isVerifying2FA}
                           >
                              Xác nhận
                           </Button>
                           <Button
                              onClick={() => {
                                 AuthStore.reset2FA()
                                 setOtpCode('')
                              }}
                           >
                              Quay lại
                           </Button>
                        </div>
                     </>
                  ) : (
                     <>
                        <div className="form__title">{t(GLOBAL_CLIENT.login)}</div>
                        <Form
                           form={form}
                           name="basic"
                           onFinish={debounce(onFinish, 400)}
                           onFinishFailed={onFinishFailed}
                           className="gx-signin-form gx-form-row0 login-form__content"
                        >
                           <Form.Item rules={[rule.emailRule, rule.requiredEmail]} name="email">
                              <Input placeholder={t(GLOBAL_CLIENT.email)} prefix={<UserOutlined />} maxLength={254} />
                           </Form.Item>
                           <Form.Item rules={[rule.requiredPassword]} name="password">
                              <Input.Password
                                 placeholder={t(GLOBAL_CLIENT.password)}
                                 prefix={<LockOutlined />}
                                 maxLength={30}
                              />
                           </Form.Item>
                           <Form.Item name="rememberMe" valuePropName="checked">
                              <>
                                 <Checkbox checked={rememberMe} onChange={onChangeRememberMe}>
                                    {t(GLOBAL_CLIENT.rememberMe)}
                                 </Checkbox>
                                 <Link className="gx-login-form-forgot" to="/forgot-password">
                                    {t(GLOBAL_CLIENT.forgotPassword)}
                                 </Link>
                              </>
                           </Form.Item>
                           <Form.Item>
                              <BaseButton type="primary" className="gx-mb-0">
                                 {t(GLOBAL_CLIENT.login)}
                              </BaseButton>
                           </Form.Item>
                        </Form>
                     </>
                  )}
               </div>
            </div>
         </LoginWrapper>
      </AuthenWrapper>
   )
})

export default LoginScreen
